/**
 * EduTool Bridge — Context detection
 *
 * Detects whether the tool is running:
 *   - inside the Eduverse KMP mobile app (WebView with JavascriptInterface)
 *   - inside the Eduverse web platform (iframe)
 *   - standalone (browser tab, no host)
 *
 * This detection runs once at bootstrap and drives the bridge strategy.
 */

export type BridgeContext = 'kmp-webview' | 'eduverse-iframe' | 'standalone';

interface WindowWithKmp extends Window {
  EduverseBridge?: {
    postMessage: (json: string) => void;
    getAuthToken?: () => string | null;
    getPlatform?: () => string;
    getContext?: () => string;
  };
  webkit?: {
    messageHandlers?: {
      EduverseBridge?: {
        postMessage: (message: unknown) => void;
      };
    };
  };
}

export function detectContext(): BridgeContext {
  if (typeof window === 'undefined') return 'standalone';

  const w = window as WindowWithKmp;

  // 1. Check for KMP Android bridge (JavascriptInterface)
  if (w.EduverseBridge && typeof w.EduverseBridge.postMessage === 'function') {
    return 'kmp-webview';
  }

  // 2. Check for KMP iOS bridge (WKScriptMessageHandler)
  if (w.webkit?.messageHandlers?.EduverseBridge) {
    return 'kmp-webview';
  }

  // 3. Check if running in an iframe (Eduverse web)
  try {
    if (window.self !== window.top) {
      return 'eduverse-iframe';
    }
  } catch {
    // Cross-origin access error — we're in an iframe
    return 'eduverse-iframe';
  }

  // 4. Otherwise standalone
  return 'standalone';
}

export function isKmpWebView(): boolean {
  return detectContext() === 'kmp-webview';
}

export function isEduverseIframe(): boolean {
  return detectContext() === 'eduverse-iframe';
}

export function isStandalone(): boolean {
  return detectContext() === 'standalone';
}

/**
 * Returns the auth token if available synchronously (KMP Android only).
 * Returns null otherwise — use async bridge flow (EDUTOOL_REQUEST_AUTH).
 */
export function getSyncAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  const w = window as WindowWithKmp;
  if (w.EduverseBridge?.getAuthToken) {
    return w.EduverseBridge.getAuthToken();
  }
  return null;
}

/**
 * Returns the native platform name if running in KMP WebView ("android" | "ios").
 */
export function getNativePlatform(): 'android' | 'ios' | null {
  if (typeof window === 'undefined') return null;
  const w = window as WindowWithKmp;

  if (w.EduverseBridge?.getPlatform) {
    const platform = w.EduverseBridge.getPlatform();
    if (platform === 'android' || platform === 'ios') return platform;
  }

  // iOS detection fallback (WKWebView exposes webkit.messageHandlers)
  if (w.webkit?.messageHandlers?.EduverseBridge) {
    return 'ios';
  }

  return null;
}
